import React, { useMemo, useContext, useEffect, useState } from 'react';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Alert, Typography, Link } from '@one-thd/sui-atomic-components';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { CustomerAccountNavigation, useAccountCrumbs, } from '@thd-olt-component-react/customer-account-navigation';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ProfileProvider } from '../contexts/profile-context';
import { PageTitle } from '../components/shared/page-title/page-title';
import { ERR_GENERIC } from '../constants/response-error-messages';
import Spinner from '../components/shared/spinner/spinner';
import { useProfile } from '../hooks/use-profile';
import { setPageErrorInfo } from '../utils/analytics';
import { getHyphenatedPhoneNumber, getReCaptchaKey } from '../utils/general';
import { SectionHeader } from '../components/shared/section-header/section-header';
import { B2C_ACCOUNT_PREFERENCE, B2C_ACCOUNT_PRIVACY_SECURITY, B2C_ACCOUNT_SECURITY, } from '../constants/links';
import { useInterceptors } from '../hooks/use-interceptors';
import { AccountClosureForm } from '../components/account-closure-form/account-closure-form';
import { mapCrumbsToStaticCrumbs, } from '../utils/breadcrumbs';
import { setCloseAccountCancelLinkAnalytics, setCloseAccountPageLinksAnalytics, setCloseAccountViewPageAnalytics, } from '../utils/close-account-page-analytics';
var crumbs = [
    {
        path: '/myaccount/profile',
        label: 'Profile',
    },
    {
        label: 'Close Account',
        path: '',
    },
];
var CloseAccountPageContent = function () {
    var _a = useState(null), generalError = _a[0], setGeneralError = _a[1];
    var isConsumerApp = useContext(ExperienceContext).isConsumerApp;
    var profileValues = useProfile();
    var getProfileData = profileValues.getProfileData, customerData = profileValues.customerData;
    var _b = useState(true), isLoading = _b[0], setLoading = _b[1];
    var staticCrumbs = mapCrumbsToStaticCrumbs(useAccountCrumbs(crumbs));
    useEffect(function () {
        setLoading(true);
        getProfileData()
            .catch(function () {
            setGeneralError(ERR_GENERIC);
            setPageErrorInfo('error', 'page', 'close account page', ERR_GENERIC.message, 'n/a');
        })
            .finally(function () {
            setLoading(false);
        });
    }, []);
    useEffect(function () {
        setCloseAccountViewPageAnalytics();
    }, []);
    var phoneNumber = useMemo(function () {
        var phones = (customerData === null || customerData === void 0 ? void 0 : customerData.phones) || [];
        var profilePhone = phones.find(function (phone) { return phone.primaryFlag === 'Y'; }) ||
            phones.find(function (phone) { return phone.primaryFlag === 'N'; });
        return (profilePhone === null || profilePhone === void 0 ? void 0 : profilePhone.number) || '';
    }, [customerData]);
    var redirectError = useInterceptors().redirectError;
    var openProfilePage = function () {
        setCloseAccountCancelLinkAnalytics();
        window.location.assign('/myaccount/profile');
    };
    var handleCloseAccountLinks = function (closeAccountPageLink) {
        setCloseAccountPageLinksAnalytics(closeAccountPageLink);
    };
    return (React.createElement("div", { className: "grid isBound" },
        React.createElement("div", { className: "col__12-12" },
            React.createElement(Spinner, { active: isLoading }),
            React.createElement(React.Fragment, null,
                React.createElement("div", { className: "sui-grid sui-gap-3 sui-py-3" },
                    !isConsumerApp && (React.createElement(ErrorBoundary, { name: "breadcrumbs" },
                        React.createElement(Breadcrumbs, { staticCrumbs: staticCrumbs }))),
                    React.createElement(PageTitle, { title: "Profile" })),
                React.createElement("div", { className: "sui-grid sui-grid-cols-12 sui-gap-5 sui-py-3" },
                    React.createElement("div", { className: "sui-col-span-12 lg:sui-col-span-3" },
                        React.createElement(ErrorBoundary, { name: "customer-account-navigation" },
                            React.createElement(CustomerAccountNavigation, null))),
                    redirectError && (React.createElement("div", { className: "sui-pt-8 sui-col-span-12 lg:sui-col-span-9" },
                        React.createElement(Alert, { status: "info" }, redirectError.message))),
                    generalError ? (React.createElement("div", { className: "sui-col-span-12 lg:sui-col-span-9" },
                        React.createElement(Alert, { status: "error" }, generalError.message))) : (React.createElement("div", { className: "sui-col-span-12 lg:sui-col-span-5 sui-leading-relaxed" }, !!customerData && (React.createElement("div", null,
                        React.createElement(ErrorBoundary, { name: "close-account-page-title-section" },
                            React.createElement(SectionHeader, { title: "Close Account", actionLabel: "Cancel", onActionClick: openProfilePage, "data-testid": "cancelLink" })),
                        React.createElement("div", { className: "sui-mb-6" },
                            React.createElement(ErrorBoundary, { name: "before-you-go-section" },
                                React.createElement("div", { className: "sui-mb-2" },
                                    React.createElement(Typography, { weight: "bold", variant: "body-base" }, "Before You Go")),
                                React.createElement("div", null,
                                    React.createElement("div", { className: "sui-mb-2" },
                                        React.createElement(Typography, { weight: "regular", variant: "body-base" }, "Once you close your Home Depot account, it can't be undone. Before you continue, consider some of these options instead:")),
                                    React.createElement("div", { className: "sui-ml-6" },
                                        React.createElement(Typography, { weight: "regular", variant: "body-base", component: "div" },
                                            React.createElement("ul", { className: "sui-list-disc" },
                                                React.createElement("li", { className: "sui-mb-2" },
                                                    React.createElement(Link, { href: B2C_ACCOUNT_PREFERENCE, onClick: function () {
                                                            return handleCloseAccountLinks('update your communication preferences link');
                                                        }, "data-testid": "communicationLink" }, "Update your Communication Preferences"),
                                                    ' ',
                                                    "if you're receiving too many notifications"),
                                                React.createElement("li", { className: "sui-mb-2" },
                                                    "Update your sign-in details on the",
                                                    ' ',
                                                    React.createElement(Link, { href: B2C_ACCOUNT_SECURITY, onClick: function () {
                                                            return handleCloseAccountLinks('security and password page link');
                                                        } }, "Security & Password page")),
                                                React.createElement("li", null,
                                                    "Visit our",
                                                    ' ',
                                                    React.createElement(Link, { href: B2C_ACCOUNT_PRIVACY_SECURITY, onClick: function () {
                                                            return handleCloseAccountLinks('privacy and security center link');
                                                        } }, "Privacy & Security Center"),
                                                    ' ',
                                                    "to exercise your privacy rights or submit a request to delete your data"))))))),
                        React.createElement("div", { className: "sui-mb-6" },
                            React.createElement(ErrorBoundary, { name: "account-details-section" },
                                React.createElement("div", { className: "sui-mb-1" },
                                    React.createElement(Typography, { weight: "bold", variant: "body-base" }, "Account Details")),
                                React.createElement("div", { className: "sui-mb-4" },
                                    React.createElement(Typography, { weight: "regular", variant: "body-base" },
                                        React.createElement("div", { "data-testid": "email", className: "sui-mb-2" }, (customerData === null || customerData === void 0 ? void 0 : customerData.emailId) || ''),
                                        React.createElement("div", { "data-testid": "phoneNumber" }, getHyphenatedPhoneNumber(phoneNumber)))))),
                        React.createElement("div", { className: "sui-mb-8" },
                            React.createElement(ErrorBoundary, { name: "important-info-section" },
                                React.createElement("div", { className: "sui-mb-1" },
                                    React.createElement(Typography, { weight: "bold", variant: "body-base" }, "Important Information")),
                                React.createElement("div", { className: "sui-mb-4" },
                                    React.createElement("div", { className: "sui-mb-2" },
                                        React.createElement(Typography, { weight: "regular", variant: "body-base" }, "Before closing this account, please be aware that:")),
                                    React.createElement("div", { className: "sui-ml-6" },
                                        React.createElement(Typography, { weight: "regular", variant: "body-base", component: "div" },
                                            React.createElement("ul", { className: "sui-list-disc" },
                                                React.createElement("li", { className: "sui-mb-2" }, "You will no longer be able to log into this account on homedepot.com."),
                                                React.createElement("li", null, "If you are a user on a Pro Xtra account with this email address, you will be removed as a user from that account as well."),
                                                React.createElement("li", { className: "sui-mb-2" }, "Your email address, phone number, shipping address, perks, receipts, and credit card information connected to this account will be removed."),
                                                React.createElement("li", { className: "sui-mb-2" }, "This request may take 24-48 hours to complete."),
                                                React.createElement("li", null, "Once you close this account, it cannot be undone."))))))),
                        React.createElement("div", { className: "sui-col-start-1" },
                            React.createElement(ErrorBoundary, { name: "account-closure-form" },
                                React.createElement(AccountClosureForm, { onCancel: openProfilePage }))))))))))));
};
export var CloseAccountPage = function (_a) {
    var customerType = _a.customerType;
    var isB2B = customerType.toUpperCase() === 'B2B';
    useEffect(function () {
        if (isB2B) {
            window.location.replace('/');
        }
    }, [isB2B]);
    if (isB2B) {
        return null;
    }
    return (React.createElement(GoogleReCaptchaProvider, { reCaptchaKey: getReCaptchaKey() },
        React.createElement(ProfileProvider, { isB2B: isB2B },
            React.createElement(QueryProvider, { cacheKey: "account-closure-page" },
                React.createElement(CloseAccountPageContent, null)))));
};
CloseAccountPageContent.dataModel = extend(CustomerAccountNavigation, Breadcrumbs);
CloseAccountPage.displayName = '';
